/** @jsx jsx */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  ReactNode,
} from 'react';
import { Link } from '@zapier/common-components';
import { jsx } from '@emotion/core';
import {
  addParamsToUrl,
  extractParams,
  removeParamsFromUrl,
} from '@zapier/url-utils';

type SSOStartUrlParamsContextType = {
  /*
    The url to redirect the user to when the account is connected. Backend defaults
    to `settings.SSO_DEFAULT_REDIRECT_URL` (/app/dashboard)
  */
  next?: string;

  /*
    Whether the backend needs to create an Authentication model for the user. This
    is best utilizied in conjunction with `scopes_selected_api`.
  */
  build_auths?: boolean;

  /*
    The selected API that we'll use lookup scopes to include when building the
    Authentication record for the user.
  */
  scopes_selected_api?: string;

  /*
    In some scenarios we delay triggering the new users onboarding to ensure
    data is in the correct state when we decide which flow to send them.
  */
  should_delay_email_onboarding?: boolean;
};

export const SSOStartUrlParamsContext = createContext<
  SSOStartUrlParamsContextType
>({
  build_auths: false,
});

export type SSOButtonProps = {
  children: ReactNode;
  label?: string;
  href: string;
  next?: string;
  onTrackSignupConversion?: (customUserId: string) => void | Promise<void>;
  onTrackSignupIntent?: () => void;
  passThrough?: boolean;
  shouldOpenInChildWindow?: boolean;
};

const SSOButton = ({
  children,
  href,
  next,
  shouldOpenInChildWindow,
  passThrough,
  onTrackSignupIntent,
  onTrackSignupConversion,
}: SSOButtonProps) => {
  const ssoStartUrlParams = useContext(SSOStartUrlParamsContext);
  const ssoStartUrl = addParamsToUrl(href, {
    ...ssoStartUrlParams,
    in_child_window: shouldOpenInChildWindow,
  });
  const onAuthComplete = useCallback(
    async (event: MessageEvent) => {
      const { origin } = window.location;

      if (event.origin !== origin) {
        console.error(
          'Auth completion message received from invalid origin',
          event
        );
        return;
      }

      let data = {};

      try {
        data = JSON.parse(event.data as string);

        if (typeof data === 'string') {
          data = JSON.parse(data);
        }
      } catch (e) {
        return;
      }

      const { nextUrl } = data as { nextUrl: string };

      if (!nextUrl) {
        console.error('Error deciphering next URL from posted window message.');
        return;
      }

      const params = extractParams(nextUrl) as {
        new_signup: string;
        customuser_id: string;
      };

      if (params.new_signup && onTrackSignupConversion) {
        try {
          await onTrackSignupConversion(params.customuser_id);
        } catch (e) {
          console.error(e);
        }
      }

      const targetUrl = removeParamsFromUrl(nextUrl, ['customuser_id']);
      const redirectUrl = addParamsToUrl('/redirect', {
        next: targetUrl,
        safe_only: true,
        fallback: 'dashboard',
      });

      window.location = redirectUrl as any;
    },
    [onTrackSignupConversion]
  );
  const onMessage = useCallback(
    (event: MessageEvent) => {
      // TODO: ignore all other messages but complete
      onAuthComplete(event);
    },
    [onAuthComplete]
  );
  useEffect(() => {
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [onMessage]);
  const onClick = useCallback(() => {
    if (passThrough && next) {
      window.location.href = next;
    }

    if (onTrackSignupIntent) {
      onTrackSignupIntent();
    }

    if (shouldOpenInChildWindow) {
      window.addEventListener('message', onMessage);
      window.open(
        ssoStartUrl,
        'popupwindow',
        'height=600,width=980,resizable=yes,scrollbars=yes'
      );
    } else {
      window.location.href = ssoStartUrl as any;
    }
  }, [
    next,
    onMessage,
    onTrackSignupIntent,
    passThrough,
    shouldOpenInChildWindow,
    ssoStartUrl,
  ]);
  return (
    <Link onClick={onClick} underline="never">
      {children}
    </Link>
  );
};

SSOButton.defaultProps = {
  passThrough: false,
  shouldOpenInChildWindow: true,

  onTrackSignupIntent() {},

  onTrackSignupConversion() {},
};
export default SSOButton;
